/**
 * @fileoverview Main Sudoku Game Hook
 * @description Manages all Sudoku game state and logic
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { Alert } from "react-native";
import {
  SudokuGameState,
  SudokuDifficulty,
  SudokuMove,
  SudokuResult,
  CellValue,
  DIFFICULTY_CONFIGS,
  SudokuWeekendConfig,
  DEFAULT_WEEKEND_CONFIG,
} from "../types";
import {
  isCorrectValue,
  isPuzzleComplete,
  getHintCell,
  getRelatedIndices,
  getCellsWithValue,
} from "../utils/sudokuValidator";
import { useSudokuPuzzles } from "./useSudokuPuzzles";
import { useGameSession } from "@/modules/games/hooks/useGameSession";
import { useAuth } from "@/providers/AuthProvider";
import { SessionMode } from "@/modules/games/types";

interface UseSudokuParams {
  /** Initial session ID to resume */
  sessionId?: string;
  /** Game mode */
  mode: SessionMode;
  /** Weekend config for points (only for weekend mode) */
  weekendConfig?: SudokuWeekendConfig;
}

interface UseSudokuReturn {
  /** Current game state */
  gameState: SudokuGameState | null;
  /** Is the game loading */
  isLoading: boolean;
  /** Error message */
  error: string | null;
  /** Selected cell index (0-80) */
  selectedCell: number | null;
  /** Is notes mode active */
  isNotesMode: boolean;
  /** Timer value in seconds */
  timerSeconds: number;
  /** Is game active (timer running) */
  isGameActive: boolean;
  /** Is game paused */
  isPaused: boolean;
  /** Session ID if created */
  sessionId: string | null;
  /** Start a new game with difficulty */
  startNewGame: (difficulty: SudokuDifficulty) => Promise<void>;
  /** Resume existing session */
  resumeGame: (sessionId: string) => Promise<void>;
  /** Select a cell */
  selectCell: (index: number) => void;
  /** Enter a number (1-9) or clear (0) */
  enterNumber: (num: number) => void;
  /** Toggle notes mode */
  toggleNotesMode: () => void;
  /** Use a hint */
  useHint: () => void;
  /** Undo last move */
  undoLastMove: () => void;
  /** Pause the game */
  pauseGame: () => void;
  /** Resume the game */
  resumeGamePlay: () => void;
  /** Get highlighted cells for current selection */
  getHighlightedCells: () => number[];
  /** Get cells with same number as selected */
  getSameNumberCells: () => number[];
  /** Get remaining hints */
  hintsRemaining: number;
  /** Get remaining mistakes before game over */
  mistakesRemaining: number;
  /** Get result (when game ends) */
  result: SudokuResult | null;
  /** Clear the game */
  clearGame: () => void;
}

/**
 * Main hook for Sudoku game logic
 */
export const useSudoku = ({
  sessionId: initialSessionId,
  mode,
  weekendConfig = DEFAULT_WEEKEND_CONFIG,
}: UseSudokuParams): UseSudokuReturn => {
  const { activeProfile, userTeam } = useAuth();
  const { fetchRandomPuzzle } = useSudokuPuzzles();
  const {
    session,
    createSession,
    loadSession,
    updateGameState: updateSessionGameState,
    completeGame: completeSessionGame,
    clearSession,
    isLoading: isSessionLoading,
  } = useGameSession(initialSessionId);

  // Local game state (synced with session)
  const [gameState, setGameState] = useState<SudokuGameState | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [result, setResult] = useState<SudokuResult | null>(null);

  // Timer
  const [timerSeconds, setTimerSeconds] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Computed values
  const isGameActive = gameState !== null && !gameState.is_completed && !gameState.is_failed && !isPaused;
  const sessionId = session?.id || null;

  const config = gameState
    ? DIFFICULTY_CONFIGS[gameState.difficulty]
    : DIFFICULTY_CONFIGS.easy;

  const hintsRemaining = config.max_hints - (gameState?.hints_used || 0);
  const mistakesRemaining = config.max_mistakes - (gameState?.mistakes || 0);

  /**
   * Start/stop timer based on game state
   */
  useEffect(() => {
    if (isGameActive && !timerRef.current) {
      timerRef.current = setInterval(() => {
        setTimerSeconds((prev) => prev + 1);
      }, 1000);
    } else if (!isGameActive && timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isGameActive]);

  /**
   * Sync game state from session when loaded
   */
  useEffect(() => {
    if (session?.game_state && Object.keys(session.game_state).length > 0) {
      const state = session.game_state as unknown as SudokuGameState;
      setGameState(state);
      setTimerSeconds(state.time_elapsed_seconds || 0);
    }
  }, [session?.game_state]);

  /**
   * Save game state to session periodically
   */
  const saveToSession = useCallback(
    async (state: SudokuGameState) => {
      if (session?.id) {
        const stateWithTime = {
          ...state,
          time_elapsed_seconds: timerSeconds,
        };
        await updateSessionGameState(stateWithTime);
      }
    },
    [session?.id, timerSeconds, updateSessionGameState]
  );

  /**
   * Start a new game with the given difficulty
   */
  const startNewGame = useCallback(
    async (difficulty: SudokuDifficulty) => {
      setIsLoading(true);
      setError(null);
      setResult(null);
      setTimerSeconds(0);
      setIsPaused(false);

      try {
        // Fetch a random puzzle
        const puzzle = await fetchRandomPuzzle(difficulty);
        if (!puzzle) {
          throw new Error("Kon geen puzzel ophalen");
        }

        // Create initial game state
        const initialState: SudokuGameState = {
          puzzle_id: puzzle.id,
          difficulty: puzzle.difficulty,
          puzzle: puzzle.puzzle,
          solution: puzzle.solution,
          current: puzzle.puzzle,
          notes: {},
          moves: [],
          mistakes: 0,
          hints_used: 0,
          selected_cell: null,
          is_notes_mode: false,
          started_at: new Date().toISOString(),
          completed_at: null,
          is_completed: false,
          is_failed: false,
          time_elapsed_seconds: 0,
        };

        // Create game session
        const sessionMode: SessionMode = mode === "weekend" ? "weekend" : "free";
        const newSession = await createSession({
          game_slug: "sudoku",
          mode: sessionMode,
          session_type: "solo",
          initial_state: initialState as unknown as Record<string, unknown>,
        });

        if (!newSession) {
          throw new Error("Kon geen sessie aanmaken");
        }

        setGameState(initialState);
      } catch (err) {
        const message = err instanceof Error ? err.message : "Fout bij starten spel";
        setError(message);
        console.error("[useSudoku] startNewGame error:", err);
      } finally {
        setIsLoading(false);
      }
    },
    [fetchRandomPuzzle, createSession, mode]
  );

  /**
   * Resume an existing game
   */
  const resumeGame = useCallback(
    async (resumeSessionId: string) => {
      setIsLoading(true);
      setError(null);

      try {
        await loadSession(resumeSessionId);
        setIsPaused(false);
      } catch (err) {
        const message = err instanceof Error ? err.message : "Kon spel niet laden";
        setError(message);
      } finally {
        setIsLoading(false);
      }
    },
    [loadSession]
  );

  /**
   * Select a cell
   */
  const selectCell = useCallback((index: number) => {
    if (!gameState || gameState.is_completed || gameState.is_failed) return;

    // Check if cell is given (pre-filled)
    const isGiven = gameState.puzzle[index] !== "0";
    
    setGameState((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        selected_cell: index,
      };
    });
  }, [gameState]);

  /**
   * Check for game over conditions
   */
  const checkGameOver = useCallback(
    async (state: SudokuGameState) => {
      const diffConfig = DIFFICULTY_CONFIGS[state.difficulty];

      // Check if failed (too many mistakes)
      if (state.mistakes >= diffConfig.max_mistakes) {
        const failedState: SudokuGameState = {
          ...state,
          is_failed: true,
          completed_at: new Date().toISOString(),
          time_elapsed_seconds: timerSeconds,
        };

        setGameState(failedState);
        await saveToSession(failedState);

        const failResult: SudokuResult = {
          completed: false,
          failed: true,
          time_seconds: timerSeconds,
          mistakes: state.mistakes,
          hints_used: state.hints_used,
          is_perfect: false,
          difficulty: state.difficulty,
          points_earned: 0,
        };

        setResult(failResult);

        // Complete session with no points
        await completeSessionGame(
          { final_scores: {}, stats: { is_win: false } },
          0
        );

        return true;
      }

      // Check if completed
      if (isPuzzleComplete(state.current, state.solution)) {
        const completedState: SudokuGameState = {
          ...state,
          is_completed: true,
          completed_at: new Date().toISOString(),
          time_elapsed_seconds: timerSeconds,
        };

        setGameState(completedState);
        await saveToSession(completedState);

        // Calculate points
        const isPerfect = state.mistakes === 0 && state.hints_used === 0;
        let points = 0;

        if (mode === "weekend") {
          points = weekendConfig.points_per_difficulty[state.difficulty];
          if (isPerfect) {
            points += weekendConfig.perfect_bonus;
          }
        }

        const winResult: SudokuResult = {
          completed: true,
          failed: false,
          time_seconds: timerSeconds,
          mistakes: state.mistakes,
          hints_used: state.hints_used,
          is_perfect: isPerfect,
          difficulty: state.difficulty,
          points_earned: points,
        };

        setResult(winResult);

        // Complete session with points
        await completeSessionGame(
          {
            winner_user_id: activeProfile?.id,
            winner_team_id: userTeam?.id,
            final_scores: { [activeProfile?.id || ""]: timerSeconds },
            stats: {
              is_win: true,
              time_seconds: timerSeconds,
              mistakes: state.mistakes,
              hints_used: state.hints_used,
              is_perfect: isPerfect,
            },
          },
          points
        );

        return true;
      }

      return false;
    },
    [timerSeconds, mode, weekendConfig, saveToSession, completeSessionGame, activeProfile?.id, userTeam?.id]
  );

  /**
   * Enter a number (1-9) or clear (0)
   */
  const enterNumber = useCallback(
    async (num: number) => {
      if (!gameState || gameState.is_completed || gameState.is_failed) return;
      if (gameState.selected_cell === null) return;

      const cellIndex = gameState.selected_cell;
      const isGiven = gameState.puzzle[cellIndex] !== "0";

      // Can't modify given cells
      if (isGiven) return;

      // Notes mode
      if (gameState.is_notes_mode && num > 0) {
        const currentNotes = gameState.notes[cellIndex] || [];
        const newNotes = currentNotes.includes(num)
          ? currentNotes.filter((n) => n !== num)
          : [...currentNotes, num].sort();

        const move: SudokuMove = {
          cell_index: cellIndex,
          previous_value: null,
          new_value: null,
          previous_notes: currentNotes,
          new_notes: newNotes,
          is_note_move: true,
          timestamp: new Date().toISOString(),
        };

        const newState: SudokuGameState = {
          ...gameState,
          notes: {
            ...gameState.notes,
            [cellIndex]: newNotes,
          },
          moves: [...gameState.moves, move],
        };

        setGameState(newState);
        await saveToSession(newState);
        return;
      }

      // Regular number entry
      const currentValue = gameState.current[cellIndex];
      const newValue = num === 0 ? "0" : num.toString();

      // Skip if same value
      if (currentValue === newValue) return;

      // Create move for undo
      const move: SudokuMove = {
        cell_index: cellIndex,
        previous_value: currentValue === "0" ? null : (parseInt(currentValue, 10) as CellValue),
        new_value: num === 0 ? null : (num as CellValue),
        previous_notes: gameState.notes[cellIndex] || [],
        new_notes: [],
        is_note_move: false,
        timestamp: new Date().toISOString(),
      };

      // Update current puzzle string
      const currentArray = gameState.current.split("");
      currentArray[cellIndex] = newValue;
      const newCurrent = currentArray.join("");

      // Check if wrong
      let newMistakes = gameState.mistakes;
      if (num > 0 && !isCorrectValue(gameState.solution, cellIndex, num)) {
        newMistakes++;
      }

      // Clear notes for this cell when entering a number
      const newNotes = { ...gameState.notes };
      if (num > 0) {
        delete newNotes[cellIndex];
      }

      const newState: SudokuGameState = {
        ...gameState,
        current: newCurrent,
        notes: newNotes,
        moves: [...gameState.moves, move],
        mistakes: newMistakes,
      };

      setGameState(newState);
      await saveToSession(newState);

      // Check game over
      await checkGameOver(newState);
    },
    [gameState, saveToSession, checkGameOver]
  );

  /**
   * Toggle notes mode
   */
  const toggleNotesMode = useCallback(() => {
    if (!gameState || gameState.is_completed || gameState.is_failed) return;

    setGameState((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        is_notes_mode: !prev.is_notes_mode,
      };
    });
  }, [gameState]);

  /**
   * Use a hint
   */
  const useHint = useCallback(async () => {
    if (!gameState || gameState.is_completed || gameState.is_failed) return;
    if (hintsRemaining <= 0) {
      Alert.alert("Geen hints meer", "Je hebt alle hints gebruikt.");
      return;
    }

    const hintIndex = getHintCell(
      gameState.current,
      gameState.solution,
      gameState.selected_cell
    );

    if (hintIndex === -1) {
      Alert.alert("Geen hint nodig", "De puzzel is al compleet!");
      return;
    }

    const correctValue = gameState.solution[hintIndex];

    // Update current puzzle string
    const currentArray = gameState.current.split("");
    currentArray[hintIndex] = correctValue;
    const newCurrent = currentArray.join("");

    // Clear notes for this cell
    const newNotes = { ...gameState.notes };
    delete newNotes[hintIndex];

    const newState: SudokuGameState = {
      ...gameState,
      current: newCurrent,
      notes: newNotes,
      hints_used: gameState.hints_used + 1,
      selected_cell: hintIndex,
    };

    setGameState(newState);
    await saveToSession(newState);

    // Check game over (might complete)
    await checkGameOver(newState);
  }, [gameState, hintsRemaining, saveToSession, checkGameOver]);

  /**
   * Undo last move
   */
  const undoLastMove = useCallback(async () => {
    if (!gameState || gameState.is_completed || gameState.is_failed) return;
    if (gameState.moves.length === 0) return;

    const lastMove = gameState.moves[gameState.moves.length - 1];
    const newMoves = gameState.moves.slice(0, -1);

    let newCurrent = gameState.current;
    let newNotes = { ...gameState.notes };
    let newMistakes = gameState.mistakes;

    if (lastMove.is_note_move) {
      // Restore previous notes
      newNotes[lastMove.cell_index] = lastMove.previous_notes;
    } else {
      // Restore previous value
      const currentArray = newCurrent.split("");
      currentArray[lastMove.cell_index] =
        lastMove.previous_value === null ? "0" : lastMove.previous_value.toString();
      newCurrent = currentArray.join("");

      // Restore notes
      if (lastMove.previous_notes.length > 0) {
        newNotes[lastMove.cell_index] = lastMove.previous_notes;
      }

      // If the undone move was a mistake, decrease mistake count
      if (
        lastMove.new_value !== null &&
        !isCorrectValue(gameState.solution, lastMove.cell_index, lastMove.new_value)
      ) {
        newMistakes = Math.max(0, newMistakes - 1);
      }
    }

    const newState: SudokuGameState = {
      ...gameState,
      current: newCurrent,
      notes: newNotes,
      moves: newMoves,
      mistakes: newMistakes,
      selected_cell: lastMove.cell_index,
    };

    setGameState(newState);
    await saveToSession(newState);
  }, [gameState, saveToSession]);

  /**
   * Pause the game
   */
  const pauseGame = useCallback(() => {
    setIsPaused(true);
    if (gameState) {
      // Save current time
      const updatedState = {
        ...gameState,
        time_elapsed_seconds: timerSeconds,
      };
      saveToSession(updatedState);
    }
  }, [gameState, timerSeconds, saveToSession]);

  /**
   * Resume the game
   */
  const resumeGamePlay = useCallback(() => {
    setIsPaused(false);
  }, []);

  /**
   * Get highlighted cells (same row, col, box)
   */
  const getHighlightedCells = useCallback(() => {
    if (gameState?.selected_cell === null || gameState?.selected_cell === undefined) {
      return [];
    }
    return getRelatedIndices(gameState.selected_cell);
  }, [gameState?.selected_cell]);

  /**
   * Get cells with same number as selected
   */
  const getSameNumberCells = useCallback(() => {
    if (!gameState || gameState.selected_cell === null) return [];

    const selectedValue = gameState.current[gameState.selected_cell];
    if (selectedValue === "0") return [];

    return getCellsWithValue(gameState.current, parseInt(selectedValue, 10));
  }, [gameState]);

  /**
   * Clear the game
   */
  const clearGame = useCallback(() => {
    setGameState(null);
    setResult(null);
    setTimerSeconds(0);
    setIsPaused(false);
    setError(null);
    clearSession();
  }, [clearSession]);

  return {
    gameState,
    isLoading: isLoading || isSessionLoading,
    error,
    selectedCell: gameState?.selected_cell ?? null,
    isNotesMode: gameState?.is_notes_mode ?? false,
    timerSeconds,
    isGameActive,
    isPaused,
    sessionId,
    startNewGame,
    resumeGame,
    selectCell,
    enterNumber,
    toggleNotesMode,
    useHint,
    undoLastMove,
    pauseGame,
    resumeGamePlay,
    getHighlightedCells,
    getSameNumberCells,
    hintsRemaining,
    mistakesRemaining,
    result,
    clearGame,
  };
};

